import React, { useEffect, useState, useCallback } from 'react';
import {
    Card, Button, Space, Tag, Table, Empty, Collapse, Typography, Modal, Form, InputNumber, Input,
    Select, Popconfirm, Statistic, Row, Col, Alert, message
} from 'antd';
import { PlusOutlined, DollarOutlined, EditOutlined, DeleteOutlined, PrinterOutlined, StopOutlined, FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { dentalService } from '../../services';
import { printDentalPlan, printDentalReceipt } from '../Print/printDentalPlan';
import ToothChart from './ToothChart';
import DentalPlanModal from './DentalPlanModal';
import {
    ITEM_STATUSES, PAYMENT_METHODS, describeTooth, formatMoney,
    itemStatus, planStatus, installmentStatus, invoiceStatus, paymentMethod, translate
} from '../../utils/dental';

const { Text } = Typography;

// The dental side of a patient's file: the mouth as it stands today, the
// treatment plans, and the money - what each plan costs, the instalments, and
// what has come in.
//
//   canEdit     the dentist: writes plans and marks work done
//   doctorInfo  the letterhead for the printed statement
//   onCount     told how many plans are in progress, for the tab's label
const DentalTab = ({ patientId, patient, canEdit, doctorInfo, onCount }) => {
    const { t, i18n } = useTranslation();
    const [chart, setChart] = useState([]);
    const [plans, setPlans] = useState([]);
    const [procedures, setProcedures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [planModal, setPlanModal] = useState({ open: false, plan: null });
    const [paying, setPaying] = useState(null);
    const [busy, setBusy] = useState(false);
    const [paymentForm] = Form.useForm();

    const tr = (key, fallback) => translate(t, key, fallback);

    const load = useCallback(async () => {
        setLoading(true);
        // Each on its own: a problem reading the chart must not hide the money,
        // nor the other way round.
        const [chartRes, plansRes, proceduresRes] = await Promise.allSettled([
            dentalService.getPatientChart(patientId),
            dentalService.getPatientPlans(patientId),
            dentalService.getProcedures()
        ]);
        setChart(chartRes.status === 'fulfilled' ? chartRes.value.data || [] : []);
        setPlans(plansRes.status === 'fulfilled' ? plansRes.value.data || [] : []);
        setProcedures(proceduresRes.status === 'fulfilled' ? proceduresRes.value.data || [] : []);
        if (plansRes.status === 'rejected') {
            message.error(plansRes.reason?.response?.data?.message || t('error'));
        }
        setLoading(false);
    }, [patientId, t]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (onCount) onCount(plans.filter((plan) => plan.status === 'active').length);
    }, [plans, onCount]);

    // The server answers every change with the plan as it now stands.
    const replace = (plan) => setPlans((current) => current.map((row) => (row._id === plan._id ? plan : row)));

    const run = async (action) => {
        setBusy(true);
        try {
            const response = await action();
            if (response?.data?._id) replace(response.data);
            message.success(t('success'));
            return true;
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const setItemStatus = (plan, itemId, status) => run(() => dentalService.updatePlan(plan._id, {
        items: plan.items.map((item) => (item._id === itemId ? { ...item, status } : item))
    }));

    const removePlan = async (plan) => {
        setBusy(true);
        try {
            await dentalService.deletePlan(plan._id);
            setPlans((current) => current.filter((row) => row._id !== plan._id));
            message.success(t('success'));
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setBusy(false);
        }
    };

    const openPayment = (plan) => {
        // Offer what is owed next: the first instalment not yet paid, or, on a
        // plan with no schedule, everything that is left.
        const next = (plan.schedule || []).find((row) => row.status !== 'paid');
        paymentForm.resetFields();
        paymentForm.setFieldsValue({
            amount: next ? next.remaining : plan.totals.remaining,
            method: 'cash',
            paidAt: dayjs().format('YYYY-MM-DD')
        });
        setPaying(plan);
    };

    const submitPayment = async () => {
        let values;
        try {
            values = await paymentForm.validateFields();
        } catch (error) {
            return;
        }
        if (await run(() => dentalService.addPayment(paying._id, values))) setPaying(null);
    };

    const print = (plan, kind = 'plan') => {
        const opened = printDentalPlan({ plan, patient, doctorInfo, lang: i18n.language, t, kind });
        if (!opened) message.warning(t('popupBlocked'));
    };

    const printReceipt = (plan, payment) => {
        const opened = printDentalReceipt({ plan, payment, patient, doctorInfo, lang: i18n.language, t });
        if (!opened) message.warning(t('popupBlocked'));
    };

    const owed = plans
        .filter((plan) => plan.status !== 'cancelled')
        .reduce((sum, plan) => sum + plan.totals.remaining, 0);
    const overdue = plans
        .filter((plan) => plan.status === 'active')
        .flatMap((plan) => plan.schedule)
        .filter((row) => row.status === 'overdue');

    const planPanel = (plan) => ({
        key: plan._id,
        label: (
            <Space wrap size={[8, 4]}>
                <Text strong>{plan.title}</Text>
                {plan.invoiceNumber && <Text type="secondary" style={{ direction: 'ltr' }}>{plan.invoiceNumber}</Text>}
                <Tag color={planStatus.color(plan.status)}>{planStatus.label(t, plan.status)}</Tag>
                {plan.invoiceStatus && plan.status !== 'cancelled' && (
                    <Tag color={invoiceStatus.color(plan.invoiceStatus)}>{invoiceStatus.label(t, plan.invoiceStatus)}</Tag>
                )}
                <Text type="secondary">{formatMoney(plan.totals.paid)} / {formatMoney(plan.totals.total)}</Text>
                {plan.totals.remaining > 0 && plan.status !== 'cancelled' && (
                    <Tag color="volcano">{tr('remaining', 'Remaining')}: {formatMoney(plan.totals.remaining)}</Tag>
                )}
                {plan.schedule.some((row) => row.status === 'overdue') && plan.status === 'active' && (
                    <Tag color="red">{installmentStatus.label(t, 'overdue')}</Tag>
                )}
            </Space>
        ),
        children: (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space wrap>
                    {plan.status !== 'cancelled' && plan.totals.remaining > 0 && (
                        <Button type="primary" icon={<DollarOutlined />} onClick={() => openPayment(plan)}>
                            {tr('dentalRecordPayment', 'Record payment')}
                        </Button>
                    )}
                    <Button icon={<FileTextOutlined />} onClick={() => print(plan, 'invoice')}>{tr('invoice', 'Invoice')}</Button>
                    <Button icon={<PrinterOutlined />} onClick={() => print(plan)}>{tr('dentalPlan', 'Treatment plan')}</Button>
                    {canEdit && (
                        <Button icon={<EditOutlined />} onClick={() => setPlanModal({ open: true, plan })}>{t('edit')}</Button>
                    )}
                    {canEdit && plan.status !== 'cancelled' && (
                        <Popconfirm
                            title={`${tr('dentalCancelPlan', 'Cancel this plan')}?`}
                            onConfirm={() => run(() => dentalService.updatePlan(plan._id, { status: 'cancelled' }))}
                        >
                            <Button icon={<StopOutlined />}>{tr('dentalCancelPlan', 'Cancel this plan')}</Button>
                        </Popconfirm>
                    )}
                    {canEdit && plan.status === 'cancelled' && (
                        <Button onClick={() => run(() => dentalService.updatePlan(plan._id, { status: 'active' }))}>
                            {tr('dentalReopenPlan', 'Re-open')}
                        </Button>
                    )}
                    {/* A plan with money against it is cancelled, never deleted. */}
                    {canEdit && plan.payments.length === 0 && (
                        <Popconfirm
                            title={t('confirmDelete')}
                            okButtonProps={{ danger: true }}
                            onConfirm={() => removePlan(plan)}
                        >
                            <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>

                {plan.totals.scheduleMismatch && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${tr('installmentMismatch', 'The instalments add up to')} ${formatMoney(plan.totals.scheduled)}, ${tr('installmentMismatchTotal', 'but the plan costs')} ${formatMoney(plan.totals.total)}.`}
                    />
                )}

                <Table
                    size="small"
                    rowKey="_id"
                    pagination={false}
                    dataSource={plan.items}
                    scroll={{ x: 520 }}
                    columns={[
                        { title: tr('tooth', 'Tooth'), dataIndex: 'tooth', width: 70, render: (value) => value || '-' },
                        { title: tr('dentalProcedureName', 'Procedure'), dataIndex: 'procedure' },
                        {
                            title: t('status'),
                            dataIndex: 'status',
                            width: 170,
                            render: (status, item) => (canEdit && plan.status !== 'cancelled' ? (
                                <Select
                                    size="small"
                                    value={status}
                                    disabled={busy}
                                    style={{ width: 150 }}
                                    onChange={(next) => setItemStatus(plan, item._id, next)}
                                    options={ITEM_STATUSES.map(([value, key, fallback]) => ({ value, label: tr(key, fallback) }))}
                                />
                            ) : <Tag color={itemStatus.color(status)}>{itemStatus.label(t, status)}</Tag>)
                        },
                        {
                            title: tr('price', 'Price'),
                            dataIndex: 'price',
                            align: 'end',
                            width: 140,
                            render: (price, item) => (
                                <Text delete={item.status === 'cancelled'}>{formatMoney(price, plan.currency)}</Text>
                            )
                        }
                    ]}
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={3}>
                                {plan.totals.discount > 0 && (
                                    <Text type="secondary">{tr('discount', 'Discount')}: {formatMoney(plan.totals.discount, plan.currency)}</Text>
                                )}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="end">
                                <Text strong>{formatMoney(plan.totals.total, plan.currency)}</Text>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    )}
                />

                {plan.schedule.length > 0 && (
                    <Table
                        size="small"
                        title={() => <Text strong>{tr('installments', 'Instalments')}</Text>}
                        rowKey={(row) => row._id || `${row.dueDate}-${row.amount}`}
                        pagination={false}
                        dataSource={plan.schedule}
                        scroll={{ x: 520 }}
                        columns={[
                            { title: '#', width: 40, render: (_, __, index) => index + 1 },
                            {
                                title: tr('dueDate', 'Due date'),
                                dataIndex: 'dueDate',
                                render: (value, row) => (
                                    <Space size={4}>
                                        {dayjs(value).format('YYYY-MM-DD')}
                                        {row.isDownPayment && <Tag>{tr('downPayment', 'Down payment')}</Tag>}
                                    </Space>
                                )
                            },
                            { title: tr('amount', 'Amount'), dataIndex: 'amount', align: 'end', render: (value) => formatMoney(value, plan.currency) },
                            { title: tr('paid', 'Paid'), dataIndex: 'paid', align: 'end', render: (value) => formatMoney(value, plan.currency) },
                            {
                                title: t('status'),
                                dataIndex: 'status',
                                render: (status) => (
                                    // On a cancelled plan nothing is being chased.
                                    <Tag color={plan.status === 'cancelled' && status !== 'paid' ? 'default' : installmentStatus.color(status)}>
                                        {installmentStatus.label(t, status)}
                                    </Tag>
                                )
                            }
                        ]}
                    />
                )}

                {plan.payments.length > 0 && (
                    <Table
                        size="small"
                        title={() => <Text strong>{tr('payments', 'Payments')}</Text>}
                        rowKey="_id"
                        pagination={false}
                        dataSource={plan.payments}
                        scroll={{ x: 520 }}
                        columns={[
                            { title: tr('date', 'Date'), dataIndex: 'paidAt', render: (value) => dayjs(value).format('YYYY-MM-DD') },
                            { title: tr('amount', 'Amount'), dataIndex: 'amount', align: 'end', render: (value) => formatMoney(value, plan.currency) },
                            { title: tr('paymentMethod', 'Method'), dataIndex: 'method', render: (value) => paymentMethod.label(t, value) },
                            { title: tr('receivedBy', 'Received by'), dataIndex: 'receivedBy', render: (value) => value?.name || '-' },
                            { title: t('notes'), dataIndex: 'note', render: (value) => value || '-' },
                            {
                                width: 50,
                                render: (_, payment) => (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<PrinterOutlined />}
                                        title={tr('receipt', 'Receipt')}
                                        aria-label={tr('receipt', 'Receipt')}
                                        onClick={() => printReceipt(plan, payment)}
                                    />
                                )
                            },
                            ...(canEdit ? [{
                                width: 50,
                                render: (_, payment) => (
                                    <Popconfirm
                                        title={`${tr('dentalRemovePayment', 'Remove this payment')}?`}
                                        description={tr('dentalRemovePaymentHint', 'It is taken out of the accounts as well.')}
                                        okButtonProps={{ danger: true }}
                                        onConfirm={() => run(() => dentalService.deletePayment(plan._id, payment._id))}
                                    >
                                        <Button danger type="text" size="small" icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                )
                            }] : [])
                        ]}
                    />
                )}

                {plan.notes && <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>{plan.notes}</Text>}
            </Space>
        )
    });

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title={tr('toothChart', 'Tooth chart')} loading={loading} size="small">
                {chart.length === 0 ? (
                    <Text type="secondary">{tr('toothChartEmpty', 'Nothing has been marked on the chart yet. Teeth are marked on the visit form.')}</Text>
                ) : (
                    <>
                        <ToothChart value={chart} readOnly />
                        <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
                            {chart.map((entry) => (
                                <Text key={entry.tooth}>
                                    {describeTooth(t, entry)}
                                    {entry.visitDate && <Text type="secondary"> · {dayjs(entry.visitDate).format('YYYY-MM-DD')}</Text>}
                                </Text>
                            ))}
                        </Space>
                    </>
                )}
            </Card>

            <Card
                title={tr('dentalPlans', 'Treatment plans')}
                size="small"
                loading={loading}
                extra={canEdit && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setPlanModal({ open: true, plan: null })}>
                        {tr('dentalNewPlan', 'New treatment plan')}
                    </Button>
                )}
            >
                {plans.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tr('dentalNoPlans', 'No treatment plans')} />
                ) : (
                    <>
                        <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col xs={12} md={6}>
                                <Statistic title={tr('remaining', 'Remaining')} value={formatMoney(owed)} valueStyle={{ fontSize: 18 }} />
                            </Col>
                            {overdue.length > 0 && (
                                <Col xs={12} md={6}>
                                    <Statistic
                                        title={installmentStatus.label(t, 'overdue')}
                                        value={formatMoney(overdue.reduce((sum, row) => sum + row.remaining, 0))}
                                        valueStyle={{ fontSize: 18, color: '#cf1322' }}
                                    />
                                </Col>
                            )}
                        </Row>
                        <Collapse
                            // The plan being worked on is open on arrival.
                            defaultActiveKey={plans.filter((plan) => plan.status === 'active').slice(0, 1).map((plan) => plan._id)}
                            items={plans.map(planPanel)}
                        />
                    </>
                )}
            </Card>

            <DentalPlanModal
                open={planModal.open}
                plan={planModal.plan}
                patient={patient}
                procedures={procedures}
                onCancel={() => setPlanModal({ open: false, plan: null })}
                onSaved={() => {
                    setPlanModal({ open: false, plan: null });
                    load();
                }}
            />

            <Modal
                title={`${tr('dentalRecordPayment', 'Record payment')}${paying ? ` — ${paying.title}` : ''}`}
                open={Boolean(paying)}
                onCancel={() => setPaying(null)}
                onOk={submitPayment}
                okText={t('save')}
                confirmLoading={busy}
                destroyOnClose
            >
                {paying && (
                    <Text type="secondary">
                        {tr('remaining', 'Remaining')}: {formatMoney(paying.totals.remaining, paying.currency)}
                    </Text>
                )}
                <Form form={paymentForm} layout="vertical" style={{ marginTop: 12 }}>
                    <Form.Item
                        name="amount"
                        label={tr('amount', 'Amount')}
                        rules={[
                            { required: true, message: t('required') },
                            {
                                validator: (_, value) => (!paying || !value || value <= paying.totals.remaining
                                    ? Promise.resolve()
                                    : Promise.reject(new Error(tr('dentalPaymentTooMuch', 'That is more than is left to pay'))))
                            }
                        ]}
                    >
                        <InputNumber
                            min={1}
                            step={5000}
                            style={{ width: '100%' }}
                            formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value.replace(/,/g, '')}
                            addonAfter={paying?.currency || 'IQD'}
                        />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="method" label={tr('paymentMethod', 'Method')}>
                                <Select options={PAYMENT_METHODS.map(([value, key, fallback]) => ({ value, label: tr(key, fallback) }))} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="paidAt" label={tr('date', 'Date')}>
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="note" label={t('notes')}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default DentalTab;
